import { useEffect, useState } from "react";
import { getAccessToken } from "../../../storage/storage";
import axios from "axios";
import { FiKey, FiCheckCircle, FiAlertCircle, FiWifiOff, FiEye, FiEyeOff } from "react-icons/fi";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const STATUS_CONFIG = {
  connected: {
    badge: "bg-green-100 text-green-700 border border-green-200",
    icon: <FiCheckCircle className="text-green-600" />,
    label: "Connected",
    buttonLabel: "Update API Key",
    buttonStyle: "border border-[#586989] text-[#586989] hover:bg-[#FFF5CE]",
  },
  expired: {
    badge: "bg-red-100 text-red-700 border border-red-200",
    icon: <FiAlertCircle className="text-red-500" />,
    label: "API Key Expired",
    buttonLabel: "Update API Key",
    buttonStyle: "bg-red-500 text-white hover:bg-red-600",
  },
  not_configured: {
    badge: "bg-gray-100 text-gray-500 border border-gray-200",
    icon: <FiWifiOff className="text-gray-400" />,
    label: "Not Configured",
    buttonLabel: "Add API Key",
    buttonStyle: "bg-[#586989] text-white hover:bg-[#47546d]",
  },
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function LodgifyConnection() {
  const token = getAccessToken();

  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const fetchStatus = () => {
    setLoadingStatus(true);
    axios
      .get(`${BASE_URL}/api/lodgify/connection-status/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setStatus(res.data);
        setLoadingStatus(false);
      })
      .catch((err) => {
        console.error("Error fetching Lodgify status:", err);
        setLoadingStatus(false);
      });
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!apiKey.trim()) return;

    setSaving(true);
    setError(null);
    setSuccessMsg(null);

    axios
      .post(
        `${BASE_URL}/api/add-api-key/`,
        { api_key: apiKey },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      )
      .then((res) => {
        setSaving(false);
        setApiKey("");
        setShowForm(false);
        setSuccessMsg("API key saved successfully.");
        // Update local status if response contains it
        if (res.data.connection_status) {
          setStatus((prev) => ({ ...prev, ...res.data }));
        } else {
          fetchStatus();
        }
      })
      .catch((err) => {
        setSaving(false);
        const msg =
          err.response?.data?.error ||
          err.response?.data?.detail ||
          "Failed to save API key. Please try again.";
        setError(msg);
      });
  };

  const cfg = status ? STATUS_CONFIG[status.connection_status] || STATUS_CONFIG.not_configured : null;

  return (
    <div className="max-w-xl">
      <div className="mb-6">
        <h2 className="text-lg font-bold text-[#586989]">Lodgify Connection</h2>
        <p className="text-sm text-gray-500 mt-1">
          Connect your Lodgify account to enable booking sync and webhook processing.
        </p>
      </div>

      {/* Status card */}
      <div className="border border-gray-200 rounded-xl p-5 mb-5 bg-gray-50">
        {loadingStatus ? (
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-32" />
            <div className="h-3 bg-gray-200 rounded w-48" />
          </div>
        ) : status ? (
          <div className="space-y-4">
            {/* Badge + status */}
            <div className="flex items-center gap-3">
              {cfg.icon}
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${cfg.badge}`}>
                {status.connection_status_display || cfg.label}
              </span>
            </div>

            {/* Key info */}
            {status.api_key_configured && status.api_key_last_4 && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <FiKey className="text-gray-400" />
                <span className="font-mono tracking-widest text-gray-500">
                  ••••&nbsp;••••&nbsp;••••&nbsp;{status.api_key_last_4}
                </span>
              </div>
            )}

            {/* Expired notice */}
            {status.connection_status === "expired" && status.last_error_at && (
              <p className="text-xs text-red-500">
                Expired on {formatDate(status.last_error_at)}
              </p>
            )}

            {/* Action button */}
            <button
              onClick={() => { setShowForm((v) => !v); setError(null); setSuccessMsg(null); }}
              className={`mt-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${cfg.buttonStyle}`}
            >
              {showForm ? "Cancel" : cfg.buttonLabel}
            </button>
          </div>
        ) : (
          <p className="text-sm text-gray-400">Could not load connection status.</p>
        )}
      </div>

      {/* Success message */}
      {successMsg && !showForm && (
        <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-4">
          <FiCheckCircle className="shrink-0" />
          {successMsg}
        </div>
      )}

      {/* API key form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="border border-gray-200 rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-semibold text-gray-700">
            {status?.api_key_configured ? "Update API Key" : "Add API Key"}
          </h3>

          <div className="space-y-1">
            <label className="text-xs text-gray-500 font-medium">Lodgify API Key</label>
            <div className="relative">
              <input
                type={showKey ? "text" : "password"}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter your Lodgify API key"
                className="w-full pr-10 pl-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#586989]/30"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showKey ? <FiEyeOff size={15} /> : <FiEye size={15} />}
              </button>
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              <FiAlertCircle className="shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              disabled={saving || !apiKey.trim()}
              className="px-5 py-2 bg-[#586989] text-white text-sm font-medium rounded-lg hover:bg-[#47546d] transition-colors disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Key"}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setError(null); setApiKey(""); }}
              className="px-5 py-2 border border-gray-300 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
